import React, { useState, useEffect } from 'react';

import {
  SaveOutlined, PlusOutlined, DeleteOutlined,
  SettingOutlined, FilterOutlined, ExportOutlined
} from '@ant-design/icons';
import { useFeatureEnabled } from '@contexts/operator/PlanContext';
import { pickingListPresetService } from '@services/operator/pickingListPresetService';
import {
  Form, Input, InputNumber, Button, Card, Row, Col, Space,
  Divider, Checkbox, TimePicker, Spin, message, Alert, Typography, Segmented
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';

import PickingListPresetRule from './Rule';

const { Text } = Typography;

interface SalesChannelOption {
  id: string;
  name?: string;
  code?: string;
  [key: string]: unknown;
}

interface ShippingTypeOption {
  id: string;
  name?: string;
  code?: string;
  [key: string]: unknown;
}

interface MerchantShippingTypeRow {
  merchant_id?: string;
  shipping_type?: ShippingTypeOption;
  enabled?: boolean;
  [key: string]: unknown;
}

interface PresetRuleValue {
  id?: string;
  value: string;
}

interface PresetRule {
  id?: string;
  field: string;
  comparator: string;
  values: string[];
  _originalValues?: PresetRuleValue[];
}

interface PresetSchedule {
  id?: string;
  execution_time?: string;
  next_run_at?: string | null;
}

interface PickingListPresetRecord {
  id?: string;
  name?: string;
  priority?: number;
  merchant_id?: string | null;
  merchant?: { id?: string | null } | null;
  warehouse?: { id?: string | null } | null;
  auto_create_picking_list?: boolean;
  auto_create_picking_settings?: { options?: Record<string, any>; [key: string]: any } | null;
  picking_list_preset_rules?: Array<{
    id?: string;
    field: string;
    comparator: string;
    values?: PresetRuleValue[];
  }>;
  picking_list_preset_schedules?: PresetSchedule[];
  [key: string]: unknown;
}

interface FormContentProps {
  loading: boolean;
  setLoading: (v: boolean) => void;
  initialData?: PickingListPresetRecord | null;
  // Merchant-scoped reference data — wrapper 負責對齊到 record 的 merchant 後再傳進來
  salesChannels?: SalesChannelOption[];
  shippingTypes?: ShippingTypeOption[] | MerchantShippingTypeRow[];
  // 新建模式才用得到，編輯模式一律用 initialData.merchant/warehouse（rec 載入後 immutable）
  merchantIdForCreate?: string | null;
  warehouseIdForCreate?: string | null;
}

const FormContent: React.FC<FormContentProps> = ({
  loading,
  setLoading,
  initialData = null,
  salesChannels = [],
  shippingTypes = [],
  merchantIdForCreate = null,
  warehouseIdForCreate = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const appPickingEnabled = useFeatureEnabled('picking_lists.app_picking');
  const [rules, setRules] = useState<PresetRule[]>([]);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const isNew = !initialData;
  const initializedId = React.useRef<string | null>(null); // Track initialized record ID

  // shippingTypes 可能來自 merchantShippingTypes (with .shipping_type nested) 或直接是 ShippingTypeOption 陣列
  // Rule 子元件預期是直接的 {id, name, code}，這裡做一次 normalize。
  const normalizedShippingTypes: ShippingTypeOption[] = React.useMemo(() => {
    if (!Array.isArray(shippingTypes)) return [];
    return shippingTypes.map((row: any) => row?.shipping_type ?? row).filter(Boolean) as ShippingTypeOption[];
  }, [shippingTypes]);

  // Populate form with initial data
  // Only rely on ID to trigger initialization
  const recordId = initialData?.id;

  useEffect(() => {
    if (!recordId) {
      // Handle "New" mode or Reset
      if (initializedId.current !== 'new' && !initialData) {
        initializedId.current = 'new';
        setRules([]);
        form.resetFields();
      }
      return;
    }

    if (initializedId.current === recordId) {
      return;
    }

    initializedId.current = recordId;

    const {
      name,
      auto_create_picking_settings,
      picking_list_preset_rules = [],
      picking_list_preset_schedules = []
    } = initialData!;

    const { options = {} } = auto_create_picking_settings || {};

    const formValues = {
      name,
      priority: initialData?.priority || 0,
      // Options
      slice_size: options.slice_size || 50,
      max_items: options.max_items,
      max_quantity: options.max_quantity,
      orders_threshold: options.orders_threshold || 0,
      separate_shippings: options.separate_shippings,
      separate_channel: options.separate_channel,
      exclude_saturday: options.exclude_saturday,
      exclude_sunday: options.exclude_sunday,
      exclude_holiday: options.exclude_holiday,
      days_ago: options.days_ago || 1,
      auto_create_picking_list: !!initialData?.auto_create_picking_list,
      picking_method: options.picking_method || (appPickingEnabled ? 'app' : 'paper'),
      // Schedules
      schedules: picking_list_preset_schedules.map(s => ({
        id: s.id,
        time: s.execution_time ? dayjs(s.execution_time, 'HH:mm') : null
      }))
    };

    form.setFieldsValue(formValues);

    // Populate Rules state
    const initialRules: PresetRule[] = picking_list_preset_rules.map(r => ({
      id: r.id,
      field: r.field,
      comparator: r.comparator,
      values: (r.values || []).map(v => v.value),
      _originalValues: r.values || []
    }));
    setRules(initialRules);
  }, [recordId, form]);

  // 將 form values + 當前 rules 狀態組成 API payload
  const buildPayload = (values: Record<string, any>) => {
    const options = {
      slice_size: values.slice_size,
      max_items: values.max_items,
      max_quantity: values.max_quantity,
      orders_threshold: values.orders_threshold,
      separate_shippings: values.separate_shippings,
      separate_channel: values.separate_channel,
      exclude_saturday: values.exclude_saturday,
      exclude_sunday: values.exclude_sunday,
      exclude_holiday: values.exclude_holiday,
      days_ago: values.days_ago,
      picking_method: values.picking_method,
    };

    // Rules Attributes
    const rulesAttributes: Array<Record<string, unknown>> = rules.map(rule => {
      const ruleAttr: Record<string, any> = {
        field: rule.field,
        comparator: rule.comparator,
        picking_list_preset_rule_values_attributes: []
      };

      if (rule.id) ruleAttr.id = rule.id;

      // Handle values - mark old ones for destruction if they changed or if it's an update
      if (rule._originalValues) {
        rule._originalValues.forEach(v => {
          ruleAttr.picking_list_preset_rule_values_attributes.push({ id: v.id, _destroy: true });
        });
      }

      const valuesArray = Array.isArray(rule.values) ? rule.values : (rule.values !== undefined ? [rule.values] : []);
      valuesArray.forEach((v: string) => {
        ruleAttr.picking_list_preset_rule_values_attributes.push({ value: v });
      });

      return ruleAttr;
    });

    // Handle deleted rules
    if (initialData && initialData.picking_list_preset_rules) {
      initialData.picking_list_preset_rules.forEach(r => {
        const stillExists = rules.some(nr => nr.id === r.id);
        if (!stillExists) {
          rulesAttributes.push({ id: r.id, _destroy: true });
        }
      });
    }

    // Schedules
    const schedulesAttributes: Array<Record<string, unknown>> = [];
    const currentSchedules = values.schedules || [];
    const initialSchedules = (initialData && initialData.picking_list_preset_schedules) || [];

    currentSchedules.forEach((s: any) => {
      if (!s || !s.time) return;
      const executionTime = s.time.format('HH:mm');
      const attr: Record<string, any> = { execution_time: executionTime };
      if (s.id) attr.id = s.id;
      schedulesAttributes.push(attr);
    });

    initialSchedules.forEach(s => {
      const stillExists = currentSchedules.some((cs: any) => cs && cs.id === s.id);
      if (!stillExists) {
        schedulesAttributes.push({ id: s.id, _destroy: true });
      }
    });

    // Edit 模式：merchant_id / warehouse_id 一律用 initialData 上的值（rec 載入後 immutable），
    // 避免使用者切了 header merchant 之後把錯誤的 merchant_id 寫回這筆 preset。
    // New 模式：使用 wrapper 傳入的 merchantIdForCreate / warehouseIdForCreate
    // （由 MerchantSelect 與 OperatorAuthContext 決定）。
    const merchantId = initialData?.merchant?.id ?? initialData?.merchant_id ?? merchantIdForCreate;
    const warehouseId = initialData?.warehouse?.id ?? warehouseIdForCreate;

    return {
      name: values.name,
      priority: values.priority,
      merchant_id: merchantId,
      warehouse_id: warehouseId,
      auto_create_picking_list: values.auto_create_picking_list,
      auto_create_picking_settings: { options },
      picking_list_preset_rules_attributes: rulesAttributes,
      picking_list_preset_schedules_attributes: schedulesAttributes,
    };
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response 給呼叫者決定後續行為
  const submitValues = async (values: Record<string, any>) => {
    const payload = buildPayload(values);
    return initialData && initialData.id
      ? await pickingListPresetService.update(initialData.id, payload)
      : await pickingListPresetService.create(payload);
  };

  // 主要提交：
  // - 編輯模式：成功後導回列表頁。留在編輯頁（導向同一條 URL）不會 remount/refetch，
  //   會看到舊的 in-memory 值，使用者誤以為沒存又連按 → 送出過期子資料踩 422。
  //   導回列表強制離開本頁、列表重新載入，畫面即顯示最新值。
  // - 新建模式：成功後導向新記錄的 detail 頁（edit 即為 detail）。
  const onFinish = async (values: Record<string, any>) => {
    setLoading(true);
    try {
      const response = await submitValues(values);

      if (response.success) {
        message.success(initialData ? t('common.message.update_success') : t('common.message.create_success'));
        if (initialData) {
          navigate('/picking_lists/presets');
        } else {
          // 新建模式：initialData 此時必為 null（已被上面的 if 收窄），故只取 response.data。
          const recordId = response.data?.id;
          navigate(`/picking_lists/presets/${recordId}`, { replace: true });
        }
      } else {
        message.error(response.message || (initialData ? t('common.error.update_failed') : t('common.error.create_failed')));
      }
    } catch (error) {
      console.error('Save preset error:', error);
      message.error(t('common.error.operation_failed'));
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單（包含 rules 狀態）留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const response = await submitValues(values);

        if (response.success) {
          message.success(t('common.message.create_success'));
          form.resetFields();
          setRules([]);
          initializedId.current = 'new';
        } else {
          message.error(response.message || t('common.error.create_failed'));
        }
      } catch (error) {
        console.error('Save preset error:', error);
        message.error(t('common.error.operation_failed'));
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  const autoCreate = Form.useWatch('auto_create_picking_list', form);
  const pickingMethod = Form.useWatch('picking_method', form);

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            slice_size: 50,
            days_ago: 1,
            priority: 0,
            auto_create_picking_list: false,
            orders_threshold: 0,
            picking_method: appPickingEnabled ? 'app' : 'paper'
          }}
        >
          {/* Header Card */}
          <Card
            title={initialData ? t('operator.picking_lists.preset_form.title_edit') : t('operator.picking_lists.preset_form.title_new')}
            extra={
              <Space>
                <Button onClick={() => navigate('/picking_lists/presets')}>
                  {t('common.button.cancel')}
                </Button>
                {isNew && (
                  <Button
                    loading={savingAndAdding}
                    disabled={loading}
                    onClick={handleSaveAndAddAnother}
                  >
                    {t('common.button.save_and_add_another')}
                  </Button>
                )}
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  disabled={savingAndAdding}
                >
                  {t('common.button.save')}
                </Button>
              </Space>
            }
          >
            <Row gutter={16}>
              <Col span={18}>
                <Form.Item
                  name="name"
                  label={t('operator.picking_list_presets.list.name')}
                  rules={[{ required: true, message: t('common.validation.required') }]}
                >
                  <Input placeholder={t('common.placeholder.please_enter')} size="large" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="priority"
                  label={t('operator.picking_list_presets.form.priority')}
                  tooltip={t('operator.picking_list_presets.form.priority_help')}
                >
                  <InputNumber min={0} style={{ width: '100%' }} size="large" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Rules Section */}
          <Card
            title={<Space><FilterOutlined />{t('operator.picking_lists.preset_form.sections.rules')}</Space>}
            style={{ marginTop: 24 }}
          >
            {rules.map((rule, index) => (
              <PickingListPresetRule
                key={rule.id || index}
                rule={rule}
                salesChannels={salesChannels}
                shippingTypes={normalizedShippingTypes}
                onUpdate={(updatedRule: PresetRule) => {
                  const newRules = [...rules];
                  newRules[index] = updatedRule;
                  setRules(newRules);
                }}
                onRemove={() => {
                  setRules(rules.filter((_, i) => i !== index));
                }}
              />
            ))}
            <Button
              type="dashed"
              block
              onClick={() => setRules([...rules, { field: '', comparator: '', values: [] }])}
              icon={<PlusOutlined />}
            >
              {t('operator.picking_lists.preset_form.fields.add_rule')}
            </Button>
          </Card>

          {/* Automation Section */}
          <Card
            title={<Space><SettingOutlined />{t('operator.picking_lists.preset_form.sections.automation')}</Space>}
            style={{ marginTop: 24 }}
          >
            <Row>
              <Col span={24}>
                <Form.Item
                  name="auto_create_picking_list"
                  valuePropName="checked"
                >
                  <Checkbox>
                    <Space direction="vertical" size={0} style={{ marginLeft: 8 }}>
                      <Text strong style={{ fontSize: '16px' }}>{t('operator.picking_lists.preset_form.fields.auto_generation_enabled')}</Text>
                      <Text type="secondary">{t('operator.picking_lists.preset_form.fields.auto_generation_help')}</Text>
                    </Space>
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>

            {autoCreate && (
              <>
                <Divider />
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="picking_method"
                      label={t('shared.picking_lists.generate.options.picking_method')}
                      rules={[{ required: true }]}
                      style={{ marginBottom: 8 }}
                    >
                      <Segmented
                        block
                        options={[
                          { label: t('shared.picking_lists.generate.options.app'), value: 'app', disabled: !appPickingEnabled },
                          { label: t('shared.picking_lists.generate.options.paper'), value: 'paper' }
                        ]}
                      />
                    </Form.Item>
                    <Alert
                      message={
                        pickingMethod === 'paper'
                          ? t('shared.picking_lists.generate.options.picking_method_help_paper')
                          : t('shared.picking_lists.generate.options.picking_method_help_app')
                      }
                      type="info"
                      showIcon
                      style={{ marginBottom: 24 }}
                    />
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="schedules"
                      label={t('operator.picking_lists.preset_form.fields.execution_schedules')}
                    >
                      <Form.List name="schedules">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map(({ key, name, ...restField }) => (
                              <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                <Form.Item
                                  {...restField}
                                  name={[name, 'time']}
                                  rules={[
                                    { required: true, message: t('common.validation.required') },
                                    ({ getFieldValue }) => ({
                                      validator(_, value) {
                                        if (!value) return Promise.resolve();
                                        const schedules = getFieldValue('schedules') || [];
                                        const timeString = value.format('HH:mm');
                                        const duplicateCount = schedules.filter((s: any) => s && s.time && s.time.format('HH:mm') === timeString).length;

                                        if (duplicateCount > 1) {
                                          return Promise.reject(new Error(t('operator.picking_lists.preset_form.validation.duplicate_time')));
                                        }
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <TimePicker
                                    format="HH:mm"
                                    minuteStep={30}
                                    showNow={false}
                                    placeholder={t('operator.picking_lists.preset_form.fields.select_time')}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                                <Form.Item {...restField} name={[name, 'id']} hidden><Input /></Form.Item>
                                <Button type="text" danger onClick={() => remove(name)} icon={<DeleteOutlined />} />
                              </Space>
                            ))}
                            <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} size="small">
                              {t('operator.picking_lists.preset_form.fields.add_time')}
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={t('operator.picking_lists.preset_form.fields.exclusions')}>
                      <Space direction="vertical">
                        <Form.Item name="exclude_saturday" valuePropName="checked" noStyle>
                          <Checkbox>{t('operator.picking_lists.preset_form.fields.exclude_saturday')}</Checkbox>
                        </Form.Item>
                        <Form.Item name="exclude_sunday" valuePropName="checked" noStyle>
                          <Checkbox>{t('operator.picking_lists.preset_form.fields.exclude_sunday')}</Checkbox>
                        </Form.Item>
                        <Form.Item name="exclude_holiday" valuePropName="checked" noStyle>
                          <Checkbox>
                            {t('operator.picking_lists.preset_form.fields.exclude_holiday')}
                            <Link to="/system_settings/holidays" target="_blank" style={{ marginLeft: 8 }}>
                              <Space size={2}>
                                <ExportOutlined style={{ fontSize: '12px' }} />
                                <Text type="secondary" style={{ fontSize: '12px' }}>{t('operator.picking_lists.preset_form.fields.set_holidays')}</Text>
                              </Space>
                            </Link>
                          </Checkbox>
                        </Form.Item>
                      </Space>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="days_ago"
                      label={t('operator.picking_lists.preset_form.fields.days_ago')}
                      rules={[{ required: true }]}
                    >
                      <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="orders_threshold"
                      label={t('operator.picking_lists.preset_form.fields.generation_threshold')}
                      tooltip={t('operator.picking_lists.preset_form.fields.generation_threshold_help', { max: 10 })}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>

                {/* 波次拆分規則 */}
                <Divider orientation="left">{t('shared.picking_lists.generate.options.wave_split_title')}</Divider>
                <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  {t('shared.picking_lists.generate.options.wave_split_description')}
                </Typography.Text>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="slice_size"
                      label={t('operator.picking_lists.preset_form.fields.slice_size')}
                      rules={[{ required: true }]}
                    >
                      <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="separate_shippings" valuePropName="checked">
                      <Checkbox>{t('shared.picking_lists.generate.options.separate_by_shipping_types')}</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="separate_channel" valuePropName="checked">
                      <Checkbox>{t('shared.picking_lists.generate.options.separate_by_channel')}</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>

                {/* 揀貨單拆分規則 */}
                <Divider orientation="left">{t('shared.picking_lists.generate.options.list_split_title')}</Divider>
                <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  {t('shared.picking_lists.generate.options.list_split_description')}
                </Typography.Text>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="max_items"
                      label={t('shared.picking_lists.generate.options.max_items')}
                      tooltip={t('shared.picking_lists.generate.options.max_items_help')}
                    >
                      <InputNumber min={1} style={{ width: '100%' }} placeholder={t('common.unlimited')} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="max_quantity"
                      label={t('shared.picking_lists.generate.options.max_quantity')}
                      tooltip={t('shared.picking_lists.generate.options.max_quantity_help')}
                    >
                      <InputNumber min={1} style={{ width: '100%' }} placeholder={t('common.unlimited')} />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </Card>

          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => navigate('/picking_lists/presets')} size="large">
                {t('common.button.cancel')}
              </Button>
              {isNew && (
                <Button
                  size="large"
                  loading={savingAndAdding}
                  disabled={loading}
                  onClick={handleSaveAndAddAnother}
                >
                  {t('common.button.save_and_add_another')}
                </Button>
              )}
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                size="large"
                loading={loading}
                disabled={savingAndAdding}
              >
                {t('common.button.save')}
              </Button>
            </Space>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default FormContent;
